/**
 * VW Client Summary Service
 * 
 * This service provides optimized client data with comprehensive information
 * using the VW_Client_Summary database view. This is the most efficient way to retrieve
 * client list data as it consolidates:
 * - Client basic info (ID, code, name)
 * - Client active status (isActive)
 * - Client types (comma-separated preview)
 * - Document counts (number of documents per client)
 * - Contact counts (number of contacts per client)
 * - Note counts (number of notes per client)
 * 
 * This replaces the previous ClientTagSummaryService with enhanced functionality.
 */

import { makeAuthenticatedRequest } from './backendApiService';

const VW_CLIENT_SUMMARY_BASE_URL = '/api/client-summaries';

/**
 * VW Client Summary Service Class
 */
class VWClientSummaryService {
    /**
     * Get all clients with summary data
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of VWClientSummary objects
     */
    async getAllClients(accessToken) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                VW_CLIENT_SUMMARY_BASE_URL
            );
            return response;
        } catch (error) {
            console.error('Failed to fetch all client summaries:', error);
            throw error;
        }
    }

    /**
     * Get clients ordered by name
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of VWClientSummary objects ordered by name
     */
    async getClientsOrderedByName(accessToken) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${VW_CLIENT_SUMMARY_BASE_URL}/order-by-name`
            );
            return response;
        } catch (error) {
            console.error('Failed to fetch clients ordered by name:', error);
            throw error;
        }
    }

    /**
     * Get clients ordered by code
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of VWClientSummary objects ordered by code
     */
    async getClientsOrderedByCode(accessToken) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${VW_CLIENT_SUMMARY_BASE_URL}/order-by-code`
            );
            return response;
        } catch (error) {
            console.error('Failed to fetch clients ordered by code:', error);
            throw error;
        }
    }

    /**
     * Get clients ordered by document count (descending)
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of VWClientSummary objects ordered by document count
     */
    async getClientsOrderedByDocumentCount(accessToken) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${VW_CLIENT_SUMMARY_BASE_URL}/order-by-document-count`
            );
            return response;
        } catch (error) {
            console.error('Failed to fetch clients ordered by document count:', error);
            throw error;
        }
    }

    /**
     * Get client by ID
     * @param {string} accessToken - JWT access token
     * @param {number} id - Client ID
     * @returns {Promise<Object|null>} VWClientSummary object or null if not found
     */
    async getClientById(accessToken, id) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${VW_CLIENT_SUMMARY_BASE_URL}/${id}`
            );
            return response;
        } catch (error) {
            if (error.message && error.message.includes('404')) {
                return null;
            }
            console.error(`Failed to fetch client summary ${id}:`, error);
            throw error;
        }
    }

    /**
     * Get client by code
     * @param {string} accessToken - JWT access token
     * @param {string} clientCode - Client code
     * @returns {Promise<Object|null>} VWClientSummary object or null if not found
     */
    async getClientByCode(accessToken, clientCode) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${VW_CLIENT_SUMMARY_BASE_URL}/code/${encodeURIComponent(clientCode)}`
            );
            return response;
        } catch (error) {
            if (error.message && error.message.includes('404')) {
                return null;
            }
            console.error(`Failed to fetch client summary with code ${clientCode}:`, error);
            throw error;
        }
    }

    /**
     * Search clients by name (case-insensitive partial match)
     * @param {string} accessToken - JWT access token
     * @param {string} searchTerm - Search term for client name
     * @returns {Promise<Array>} Array of matching VWClientSummary objects
     */
    async searchClientsByName(accessToken, searchTerm) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${VW_CLIENT_SUMMARY_BASE_URL}/search?name=${encodeURIComponent(searchTerm)}`
            );
            return response;
        } catch (error) {
            console.error(`Failed to search client summaries with term "${searchTerm}":`, error);
            throw error;
        }
    }

    /**
     * Find clients by client type
     * @param {string} accessToken - JWT access token
     * @param {string} clientType - Client type to filter by
     * @returns {Promise<Array>} Array of matching VWClientSummary objects
     */
    async findClientsByType(accessToken, clientType) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${VW_CLIENT_SUMMARY_BASE_URL}/by-client-type?clientType=${encodeURIComponent(clientType)}`
            );
            return response;
        } catch (error) {
            console.error(`Failed to find client summaries by type "${clientType}":`, error);
            throw error;
        }
    }

    /**
     * Get clients without types
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of VWClientSummary objects without types
     */
    async getClientsWithoutTypes(accessToken) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${VW_CLIENT_SUMMARY_BASE_URL}/without-types`
            );
            return response;
        } catch (error) {
            console.error('Failed to fetch client summaries without types:', error);
            throw error;
        }
    }

    /**
     * Get clients with types
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of VWClientSummary objects with types
     */
    async getClientsWithTypes(accessToken) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${VW_CLIENT_SUMMARY_BASE_URL}/with-types`
            );
            return response;
        } catch (error) {
            console.error('Failed to fetch client summaries with types:', error);
            throw error;
        }
    }

    /**
     * Get clients with documents
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of VWClientSummary objects with documents
     */
    async getClientsWithDocuments(accessToken) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${VW_CLIENT_SUMMARY_BASE_URL}/with-documents`
            );
            return response;
        } catch (error) {
            console.error('Failed to fetch client summaries with documents:', error);
            throw error;
        }
    }

    /**
     * Get clients without documents
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of VWClientSummary objects without documents
     */
    async getClientsWithoutDocuments(accessToken) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${VW_CLIENT_SUMMARY_BASE_URL}/without-documents`
            );
            return response;
        } catch (error) {
            console.error('Failed to fetch client summaries without documents:', error);
            throw error;
        }
    }

    // ============= PAGINATED ENDPOINTS =============

    /**
     * Get all clients with pagination
     * @param {string} accessToken - JWT access token
     * @param {Object} params - Pagination parameters {page, size, sort}
     * @returns {Promise<Object>} Paginated response with content, totalPages, totalElements, etc.
     */
    async getAllClientsPaged(accessToken, params = {}) {
        try {
            const queryParams = new URLSearchParams({
                page: params.page?.toString() || '0',
                size: params.size?.toString() || '20',
                sort: params.sort || 'clientName,asc'
            });
            
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${VW_CLIENT_SUMMARY_BASE_URL}/paged?${queryParams.toString()}`
            );
            return response;
        } catch (error) {
            console.error('Failed to fetch paginated client summaries:', error);
            throw error;
        }
    }

    /**
     * Search clients by name (paginated)
     * @param {string} accessToken - JWT access token
     * @param {string} searchTerm - Search term for client name
     * @param {Object} params - Pagination parameters {page, size, sort}
     * @returns {Promise<Object>} Paginated response
     */
    async searchClientsByNamePaged(accessToken, searchTerm, params = {}) {
        try {
            // WORKAROUND: Try multiple case variations since backend search is case-sensitive
            // This should be fixed in the backend to use case-insensitive search
            const searchVariations = [
                searchTerm,
                searchTerm.toLowerCase(),
                searchTerm.toUpperCase(),
                searchTerm.charAt(0).toUpperCase() + searchTerm.slice(1).toLowerCase()
            ];
            
            let bestResult = null;
            
            for (const variation of searchVariations) {
                const queryParams = new URLSearchParams({
                    name: variation,
                    page: params.page?.toString() || '0',
                    size: params.size?.toString() || '20',
                    sort: params.sort || 'clientName,asc'
                });
                
                const response = await makeAuthenticatedRequest(
                    accessToken, 
                    `${VW_CLIENT_SUMMARY_BASE_URL}/search/paged?${queryParams.toString()}`
                );
                
                // If we found results, return them
                if (response.totalElements > 0) {
                    return response;
                }
                
                // Keep the first response in case none return results
                if (!bestResult) {
                    bestResult = response;
                }
            }
            
            return bestResult;
        } catch (error) {
            console.error(`Failed to search paginated client summaries with term "${searchTerm}":`, error);
            throw error;
        }
    }

    /**
     * Find clients by client type (paginated)
     * @param {string} accessToken - JWT access token
     * @param {string} clientType - Client type to filter by
     * @param {Object} params - Pagination parameters {page, size, sort}
     * @returns {Promise<Object>} Paginated response
     */
    async findClientsByTypePaged(accessToken, clientType, params = {}) {
        try {
            const queryParams = new URLSearchParams({
                clientType: clientType,
                page: params.page?.toString() || '0',
                size: params.size?.toString() || '20',
                sort: params.sort || 'clientName,asc'
            });
            
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${VW_CLIENT_SUMMARY_BASE_URL}/by-client-type/paged?${queryParams.toString()}`
            );
            return response;
        } catch (error) {
            console.error(`Failed to find paginated client summaries by type "${clientType}":`, error);
            throw error;
        }
    }

    /**
     * Get clients without types (paginated)
     * @param {string} accessToken - JWT access token
     * @param {Object} params - Pagination parameters {page, size, sort}
     * @returns {Promise<Object>} Paginated response
     */
    async getClientsWithoutTypesPaged(accessToken, params = {}) {
        try {
            const queryParams = new URLSearchParams({
                page: params.page?.toString() || '0',
                size: params.size?.toString() || '20',
                sort: params.sort || 'clientName,asc'
            });
            
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${VW_CLIENT_SUMMARY_BASE_URL}/without-types/paged?${queryParams.toString()}`
            );
            return response;
        } catch (error) {
            console.error('Failed to fetch paginated client summaries without types:', error);
            throw error;
        }
    }

    /**
     * Get clients with types (paginated)
     * @param {string} accessToken - JWT access token
     * @param {Object} params - Pagination parameters {page, size, sort}
     * @returns {Promise<Object>} Paginated response
     */
    async getClientsWithTypesPaged(accessToken, params = {}) {
        try {
            const queryParams = new URLSearchParams({
                page: params.page?.toString() || '0',
                size: params.size?.toString() || '20',
                sort: params.sort || 'clientName,asc'
            });
            
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${VW_CLIENT_SUMMARY_BASE_URL}/with-types/paged?${queryParams.toString()}`
            );
            return response;
        } catch (error) {
            console.error('Failed to fetch paginated client summaries with types:', error);
            throw error;
        }
    }
}

/**
 * Helper functions for working with VWClientSummary data
 */
export class VWClientSummaryHelper {
    /**
     * Check if client has types
     * @param {Object} client - VWClientSummary object
     * @returns {boolean}
     */
    static hasClientTypes(client) {
        return client.clientTypesPreview !== null && 
               client.clientTypesPreview !== undefined && 
               client.clientTypesPreview.trim() !== '';
    }

    /**
     * Get client types as array
     * @param {Object} client - VWClientSummary object
     * @returns {Array<string>}
     */
    static getClientTypesArray(client) {
        if (!this.hasClientTypes(client)) {
            return [];
        }
        return client.clientTypesPreview.split(', ').map(type => type.trim());
    }

    /**
     * Get display name (formatted)
     * @param {Object} client - VWClientSummary object
     * @returns {string}
     */
    static getDisplayName(client) {
        if (client.clientCode && client.clientName) {
            return `${client.clientCode} - ${client.clientName}`;
        }
        return client.clientName || client.clientCode || `Client ${client.clientId}`;
    }

    /**
     * Check if client has documents
     * @param {Object} client - VWClientSummary object
     * @returns {boolean}
     */
    static hasDocuments(client) {
        return client.documentCount > 0;
    }

    /**
     * Get document count with formatting
     * @param {Object} client - VWClientSummary object
     * @returns {string}
     */
    static getDocumentCountText(client) {
        const count = client.documentCount || 0;
        return count === 1 ? '1 document' : `${count} documents`;
    }

    /**
     * Filter clients by multiple types
     * @param {Array} clients - Array of VWClientSummary objects
     * @param {Array<string>} types - Array of type names to filter by
     * @returns {Array}
     */
    static filterClientsByTypes(clients, types) {
        return clients.filter(client => {
            const clientTypes = this.getClientTypesArray(client);
            return types.some(type => 
                clientTypes.some(clientType => 
                    clientType.toLowerCase().includes(type.toLowerCase())
                )
            );
        });
    }

    /**
     * Filter clients by document count range
     * @param {Array} clients - Array of VWClientSummary objects
     * @param {number} minCount - Minimum document count (default: 0)
     * @param {number} maxCount - Maximum document count (optional)
     * @returns {Array}
     */
    static filterClientsByDocumentCount(clients, minCount = 0, maxCount = undefined) {
        return clients.filter(client => {
            const count = client.documentCount || 0;
            return count >= minCount && (maxCount === undefined || count <= maxCount);
        });
    }

    /**
     * Extract unique client types from a list of clients
     * @param {Array} clients - Array of VWClientSummary objects
     * @returns {Array<string>}
     */
    static extractUniqueClientTypes(clients) {
        const typesSet = new Set();
        
        clients.forEach(client => {
            const types = this.getClientTypesArray(client);
            types.forEach(type => typesSet.add(type));
        });
        
        return Array.from(typesSet).sort();
    }

    /**
     * Group clients by client type
     * @param {Array} clients - Array of VWClientSummary objects
     * @returns {Object} Object with type names as keys and arrays of clients as values
     */
    static groupClientsByType(clients) {
        const groups = {};
        
        clients.forEach(client => {
            const types = this.getClientTypesArray(client);
            
            if (types.length === 0) {
                if (!groups['No Type']) {
                    groups['No Type'] = [];
                }
                groups['No Type'].push(client);
            } else {
                types.forEach(type => {
                    if (!groups[type]) {
                        groups[type] = [];
                    }
                    groups[type].push(client);
                });
            }
        });
        
        return groups;
    }

    /**
     * Get statistics about client types and documents
     * @param {Array} clients - Array of VWClientSummary objects
     * @returns {Object} Statistics object
     */
    static getClientTypeStatistics(clients) {
        const total = clients.length;
        const withTypes = clients.filter(client => this.hasClientTypes(client)).length;
        const withoutTypes = total - withTypes;
        const withDocuments = clients.filter(client => this.hasDocuments(client)).length;
        const withoutDocuments = total - withDocuments;
        const activeClients = clients.filter(client => this.isClientActive(client)).length;
        const inactiveClients = total - activeClients;
        const uniqueTypes = this.extractUniqueClientTypes(clients);
        const typeGroups = this.groupClientsByType(clients);
        
        // Calculate total document count
        const totalDocuments = clients.reduce((sum, client) => sum + (client.documentCount || 0), 0);
        const avgDocumentsPerClient = total > 0 ? (totalDocuments / total).toFixed(1) : 0;
        
        // Calculate total contact and note counts
        const totalContacts = clients.reduce((sum, client) => sum + (client.contactCount || 0), 0);
        const totalNotes = clients.reduce((sum, client) => sum + (client.noteCount || 0), 0);
        
        return {
            total,
            withTypes,
            withoutTypes,
            withDocuments,
            withoutDocuments,
            activeClients,
            inactiveClients,
            totalDocuments,
            totalContacts,
            totalNotes,
            avgDocumentsPerClient,
            uniqueTypesCount: uniqueTypes.length,
            uniqueTypes,
            typeGroups,
            typeDistribution: Object.entries(typeGroups).map(([type, clientsArray]) => ({
                type,
                count: clientsArray.length,
                percentage: ((clientsArray.length / total) * 100).toFixed(1),
                totalDocuments: clientsArray.reduce((sum, c) => sum + (c.documentCount || 0), 0),
                totalContacts: clientsArray.reduce((sum, c) => sum + (c.contactCount || 0), 0),
                totalNotes: clientsArray.reduce((sum, c) => sum + (c.noteCount || 0), 0)
            }))
        };
    }

    /**
     * Get document count statistics
     * @param {Array} clients - Array of VWClientSummary objects
     * @returns {Object} Document statistics
     */
    static getDocumentStatistics(clients) {
        const documentCounts = clients.map(c => c.documentCount || 0);
        const total = documentCounts.reduce((sum, count) => sum + count, 0);
        const clientsWithDocs = clients.filter(c => this.hasDocuments(c)).length;
        const clientsWithoutDocs = clients.length - clientsWithDocs;
        
        const max = Math.max(...documentCounts);
        const min = Math.min(...documentCounts);
        const avg = clients.length > 0 ? (total / clients.length).toFixed(1) : 0;
        
        return {
            totalDocuments: total,
            clientsWithDocuments: clientsWithDocs,
            clientsWithoutDocuments: clientsWithoutDocs,
            maxDocumentsPerClient: max,
            minDocumentsPerClient: min,
            avgDocumentsPerClient: avg
        };
    }

    /**
     * Sort clients by document count
     * @param {Array} clients - Array of VWClientSummary objects
     * @param {string} direction - 'asc' or 'desc' (default: 'desc')
     * @returns {Array} Sorted array
     */
    static sortByDocumentCount(clients, direction = 'desc') {
        return [...clients].sort((a, b) => {
            const countA = a.documentCount || 0;
            const countB = b.documentCount || 0;
            return direction === 'desc' ? countB - countA : countA - countB;
        });
    }

    /**
     * Get clients with most documents
     * @param {Array} clients - Array of VWClientSummary objects
     * @param {number} limit - Number of clients to return (default: 10)
     * @returns {Array}
     */
    static getTopClientsByDocumentCount(clients, limit = 10) {
        return this.sortByDocumentCount(clients, 'desc').slice(0, limit);
    }

    /**
     * Check if client is active
     * Handles multiple data types: boolean, number (0/1), or string ("true"/"false")
     * @param {Object} client - VWClientSummary object
     * @returns {boolean}
     */
    static isClientActive(client) {
        // Handle undefined/null
        if (client.isActive === undefined || client.isActive === null) {
            return false;
        }
        
        // Handle boolean
        if (typeof client.isActive === 'boolean') {
            return client.isActive;
        }
        
        // Handle number (1 = active, 0 = inactive)
        if (typeof client.isActive === 'number') {
            return client.isActive === 1;
        }
        
        // Handle string
        if (typeof client.isActive === 'string') {
            return client.isActive.toLowerCase() === 'true' || client.isActive === '1';
        }
        
        // Default to false for unknown types
        return false;
    }

    /**
     * Filter clients by active status
     * @param {Array} clients - Array of VWClientSummary objects
     * @param {boolean} activeOnly - If true, return only active clients; if false, return only inactive clients
     * @returns {Array}
     */
    static filterClientsByActiveStatus(clients, activeOnly = true) {
        return clients.filter(client => this.isClientActive(client) === activeOnly);
    }

    /**
     * Get contact count with formatting
     * @param {Object} client - VWClientSummary object
     * @returns {string}
     */
    static getContactCountText(client) {
        const count = client.contactCount || 0;
        return count === 1 ? '1 contact' : `${count} contacts`;
    }

    /**
     * Get note count with formatting
     * @param {Object} client - VWClientSummary object
     * @returns {string}
     */
    static getNoteCountText(client) {
        const count = client.noteCount || 0;
        return count === 1 ? '1 note' : `${count} notes`;
    }

    /**
     * Get billing contact count with formatting
     * @param {Object} client - VWClientSummary object
     * @returns {string}
     */
    static getBillingContactCountText(client) {
        const count = client.billingContactCount || 0;
        return count === 1 ? '1 billing contact' : `${count} billing contacts`;
    }

    /**
     * Check if client has billing contacts
     * @param {Object} client - VWClientSummary object
     * @returns {boolean}
     */
    static hasBillingContacts(client) {
        return (client.billingContactCount || 0) > 0;
    }

    /**
     * Convert VWClientSummary to legacy client format for backward compatibility
     * This helps with gradual migration from old ClientService
     * @param {Object} vwClientSummary - VWClientSummary object
     * @returns {Object} Legacy format client object
     */
    static toLegacyClientFormat(vwClientSummary) {
        return {
            clientId: vwClientSummary.clientId,
            clientCode: vwClientSummary.clientCode,
            clientName: vwClientSummary.clientName,
            // Use isActive from VW Client Summary
            isActive: vwClientSummary.isActive !== undefined ? vwClientSummary.isActive : true,
            createdAt: null, // This would need to come from separate API if needed
            // Add computed fields from VW
            clientTypesPreview: vwClientSummary.clientTypesPreview,
            documentCount: vwClientSummary.documentCount,
            contactCount: vwClientSummary.contactCount,
            noteCount: vwClientSummary.noteCount,
            billingContactCount: vwClientSummary.billingContactCount,
            // Add helper properties
            _hasClientTypes: this.hasClientTypes(vwClientSummary),
            _clientTypesArray: this.getClientTypesArray(vwClientSummary),
            _displayName: this.getDisplayName(vwClientSummary),
            _hasDocuments: this.hasDocuments(vwClientSummary),
            _documentCountText: this.getDocumentCountText(vwClientSummary),
            _contactCountText: this.getContactCountText(vwClientSummary),
            _noteCountText: this.getNoteCountText(vwClientSummary),
            _billingContactCountText: this.getBillingContactCountText(vwClientSummary),
            _hasBillingContacts: this.hasBillingContacts(vwClientSummary),
            _isActive: this.isClientActive(vwClientSummary)
        };
    }
}

// Create and export singleton instance
export const vwClientSummaryService = new VWClientSummaryService();

// Export as default for convenience
export default vwClientSummaryService;

// Note: Individual method exports removed to avoid conflicts with clientService
// Use: vwClientSummaryService.getAllClients(token) instead of getAllClients(token)
